/**
 * Gestion des intentions d'enrollment (enrollment intent)
 * Système inspiré des apps internationales (Coursera, Udemy, LinkedIn Learning)
 * 
 * Quand un utilisateur non connecté veut accéder à un cours, on sauvegarde
 * son intention pour compléter l'enrollment automatiquement après connexion.
 */

const ENROLLMENT_INTENT_KEY = 'enrollment_intent'
const ENROLLMENT_INTENT_EXPIRY = 24 * 60 * 60 * 1000 // 24 heures

/**
 * Structure d'une intention d'enrollment
 * @typedef {Object} EnrollmentIntent
 * @property {string} courseId - ID du cours
 * @property {string} courseTitle - Titre du cours (pour affichage)
 * @property {string} courseThumbnail - Image du cours (pour affichage)
 * @property {boolean} isFree - Si le cours est gratuit
 * @property {number} timestamp - Timestamp de création
 * @property {string} returnUrl - URL de retour après enrollment
 */

/**
 * Sauvegarde une intention d'enrollment
 * @param {Object} intent - Données de l'intention
 */
export function saveEnrollmentIntent(intent) {
  try {
    const intentData = {
      ...intent,
      timestamp: Date.now()
    }
    sessionStorage.setItem(ENROLLMENT_INTENT_KEY, JSON.stringify(intentData))
    return true
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'intention d\'enrollment:', error)
    return false
  }
}

/**
 * Récupère l'intention d'enrollment en cours
 * @returns {EnrollmentIntent|null}
 */
export function getEnrollmentIntent() {
  try {
    const stored = sessionStorage.getItem(ENROLLMENT_INTENT_KEY)
    if (!stored) return null

    const intent = JSON.parse(stored)
    
    // Vérifier l'expiration
    const now = Date.now()
    if (now - intent.timestamp > ENROLLMENT_INTENT_EXPIRY) {
      clearEnrollmentIntent()
      return null
    }

    return intent
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'intention d\'enrollment:', error)
    return null
  }
}

/**
 * Supprime l'intention d'enrollment
 */
export function clearEnrollmentIntent() {
  try {
    sessionStorage.removeItem(ENROLLMENT_INTENT_KEY)
    return true
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'intention d\'enrollment:', error)
    return false
  }
}

/**
 * Vérifie si une intention d'enrollment existe
 * @returns {boolean}
 */
export function hasEnrollmentIntent() {
  return getEnrollmentIntent() !== null
}

/**
 * Génère l'URL de login avec contexte d'enrollment
 * @param {string} courseId - ID du cours (optionnel)
 * @returns {string}
 */
export function getLoginUrlWithContext(courseId = null) {
  if (courseId) {
    return `/login?enroll=${courseId}`
  }
  return '/login?enroll=true'
}

/**
 * Génère l'URL de signup avec contexte d'enrollment
 * @param {string} courseId - ID du cours (optionnel)
 * @returns {string}
 */
export function getSignupUrlWithContext(courseId = null) {
  if (courseId) {
    return `/signup?enroll=${courseId}`
  }
  return '/signup?enroll=true'
}

